import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, RefreshCw, Loader2, Bike, Eye } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DpEarningsDashboard() {
  const [dpEarnings, setDpEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  useEffect(() => {
    fetchDpEarnings();
  }, []);

  const fetchDpEarnings = async () => {
    setLoadingEarnings(true);
    try {
      const res = await axios.get(`${API}/admin/delivery-earnings`);
      setDpEarnings(res.data);
    } catch (error) {
      toast.error('Failed to fetch delivery earnings');
    } finally {
      setLoadingEarnings(false);
    }
  };

  if (loadingEarnings) {
    return (
      <Card className="p-6 bg-white" data-testid="dp-earnings-section">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0a7d4f]" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-white" data-testid="dp-earnings-section">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#0a7d4f]" />
          Delivery Partner Earnings
        </h3>
        <Button size="sm" variant="outline" onClick={fetchDpEarnings}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {dpEarnings ? (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Total Partners</p>
              <p className="text-2xl font-bold text-green-800">{dpEarnings.total_partners}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600">Total Earnings</p>
              <p className="text-2xl font-bold text-blue-800">₹{dpEarnings.total_earnings?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600">Total Deliveries</p>
              <p className="text-2xl font-bold text-purple-800">{dpEarnings.total_deliveries}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Today's Earnings</p>
              <p className="text-2xl font-bold text-orange-800">₹{dpEarnings.today_earnings?.toLocaleString()}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Partner</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Total Earnings</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Deliveries</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Today</th>
                </tr>
              </thead>
              <tbody>
                {dpEarnings.partners?.map((partner, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{partner.name}</td>
                    <td className="px-4 py-3 text-sm">{partner.phone}</td>
                    <td className="px-4 py-3">
                      <Badge className="bg-green-100 text-green-800">
                        {partner.status || 'active'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-medium text-green-600">₹{partner.total_earnings?.toLocaleString()}</td>
                    <td className="px-4 py-3">{partner.total_deliveries}</td>
                    <td className="px-4 py-3 text-sm">₹{partner.today_earnings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Bike className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No earnings data available</p>
        </div>
      )}
    </Card>
  );
}
